import React from "react";
import { motion } from "framer-motion";
import {
  FiArrowRight,
  FiCheck,
  FiCode,
  FiMail,
  FiMonitor,
  FiShoppingBag,
  FiTool,
  FiSmartphone,
} from "react-icons/fi";
import "./App.css";

const projects = [
  {
    number: "01",
    title: "BellaSculpt",
    type: "Business Website",
    description:
      "A modern web presence designed to present a beauty and wellness business professionally.",
    tags: ["Design", "Development", "Responsive"],
  },
  {
    number: "02",
    title: "Cart Share",
    type: "Shopify Development",
    description:
      "Custom Shopify functionality focused on making product sharing and shopping easier.",
    tags: ["Shopify", "JavaScript", "E-commerce"],
  },
];

const services = [
  {
    icon: <FiMonitor />,
    title: "Business Websites",
    text: "Professional websites for small businesses that need a strong online presence.",
  },
  {
    icon: <FiShoppingBag />,
    title: "Shopify",
    text: "Shopify store setup, customization and improvements for growing businesses.",
  },
  {
    icon: <FiCode />,
    title: "Custom Websites",
    text: "Modern custom websites built with React, JavaScript and other web technologies.",
  },
  {
    icon: <FiTool />,
    title: "Website Fixes",
    text: "Already have a website? I can fix problems, improve the design or make it work better on mobile.",
  },
];

const benefits = [
  "Mobile-friendly design",
  "Clear communication",
  "Modern, professional appearance",
  "Fast and practical websites",
  "Personal service - no agency layers",
];

export default function App() {
  const scrollToWork = () => {
    document.getElementById("work")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  return (
    <div className="site">
      <div className="background-grid" />
      <div className="background-glow glow-one" />
      <div className="background-glow glow-two" />

      <header className="nav">
        <div className="nav-inner">
          <div className="brand">
            <span className="brand-name">PAVEL BELLE</span>
            <span className="brand-role">WEB DEVELOPER</span>
          </div>

          <nav>
            <button onClick={scrollToWork}>Work</button>

            <button
              onClick={() =>
                document
                  .getElementById("services")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Services
            </button>

            <button
              onClick={() =>
                document
                  .getElementById("about")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              About
            </button>

            <button className="nav-cta" onClick={scrollToContact}>
              Get a Quote
            </button>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero-content">
            <motion.div
              className="eyebrow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              WEB DEVELOPER & WEB DESIGNER
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              Websites that make
              <span> your business </span>
              look professional.
            </motion.h1>

            <motion.p
              className="hero-description"
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              I build modern, responsive websites for small businesses,
              entrepreneurs and local services - without the agency price tag.
            </motion.p>

            <motion.div
              className="hero-actions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <button className="primary-button" onClick={scrollToContact}>
                Get a Free Quote
                <FiArrowRight />
              </button>

              <button className="secondary-button" onClick={scrollToWork}>
                See My Work
              </button>
            </motion.div>

            <motion.div
              className="starting-price"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <span>Starting at</span>
              <strong>$250</strong>
              <span>for a one-page business website</span>
            </motion.div>
          </div>

          <div className="hero-card">
            <div className="hero-card-top">
              <span className="status-dot" />
              AVAILABLE FOR NEW PROJECTS
            </div>

            <div className="code-window">
              <div className="code-line">
                <span className="code-number">01</span>
                <span>
                  <em>&lt;website</em> <b>for</b>="your-business"<em>&gt;</em>
                </span>
              </div>

              <div className="code-line">
                <span className="code-number">02</span>
                <span className="indent">
                  professional<span className="accent">.</span>design
                </span>
              </div>

              <div className="code-line">
                <span className="code-number">03</span>
                <span className="indent">
                  mobile<span className="accent">.</span>responsive
                </span>
              </div>

              <div className="code-line">
                <span className="code-number">04</span>
                <span className="indent">
                  built<span className="accent">.</span>for
                  <span className="accent">.</span>business
                </span>
              </div>

              <div className="code-line">
                <span className="code-number">05</span>
                <span>
                  <em>&lt;/website&gt;</em>
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="trust-strip">
          <div>REACT</div>
          <div>WORDPRESS</div>
          <div>SHOPIFY</div>
          <div>JAVASCRIPT</div>
          <div>RESPONSIVE DESIGN</div>
        </section>

        <section className="content-section" id="work">
          <div className="section-heading">
            <span className="section-number">01 / WORK</span>
            <h2>Selected projects.</h2>
            <p>A selection of websites and web projects I've worked on.</p>
          </div>

          <div className="projects-grid">
            {projects.map((project) => (
              <motion.article
                className="project-card"
                key={project.number}
                whileHover={{ y: -8 }}
                transition={{ duration: 0.25 }}
              >
                <div className="project-number">{project.number}</div>

                <div className="project-preview">
                  <div className="preview-lines">
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>

                  <div className="preview-label">{project.type}</div>
                </div>

                <div className="project-info">
                  <span className="project-type">{project.type}</span>
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>

                  <div className="project-tags">
                    {project.tags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        <section
          className="content-section services-section"
          id="services"
        >
          <div className="section-heading">
            <span className="section-number">02 / SERVICES</span>
            <h2>What I can build for you.</h2>
            <p>
              From a simple business website to a custom web project.
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                className="service-card"
                key={service.title}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <div className="service-icon">{service.icon}</div>

                <h3>{service.title}</h3>

                <p>{service.text}</p>

                <span className="service-arrow">↗</span>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="pricing-section">
          <div className="pricing-card">
            <div className="pricing-main">
              <span className="section-number">SIMPLE START</span>

              <h2>Need a simple website?</h2>

              <p>
                Get a clean, professional one-page website without paying
                agency prices.
              </p>

              <div className="price">
                <span>from</span>
                <strong>$250</strong>
              </div>

              <button className="primary-button" onClick={scrollToContact}>
                Tell Me About Your Project
                <FiArrowRight />
              </button>
            </div>

            <div className="pricing-features">
              {[
                "One-page business website",
                "Responsive mobile design",
                "Contact form",
                "Social media links",
                "Basic SEO setup",
                "Deployment",
                "1-2 rounds of revisions",
              ].map((item) => (
                <div className="feature" key={item}>
                  <FiCheck />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="pricing-note">
            Complex functionality, e-commerce, booking systems and
            integrations are quoted separately.
          </p>
        </section>

        <section className="content-section about-section" id="about">
          <div className="about-layout">
            <div>
              <span className="section-number">03 / ABOUT</span>

              <h2>A developer who thinks about the business, too.</h2>
            </div>

            <div className="about-text">
              <p>
                I'm Pavel Belle, a web developer and designer focused on
                building practical websites for small businesses and
                entrepreneurs.
              </p>

              <p>
                I work with React, WordPress, Shopify and modern web
                technologies. My goal isn't to make a website complicated -
                it's to make it look professional, work well and give your
                customers a clear reason to trust your business.
              </p>

              <div className="benefits">
                {benefits.map((benefit) => (
                  <div className="benefit" key={benefit}>
                    <FiCheck />
                    {benefit}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="content-section process-section">
          <div className="section-heading">
            <span className="section-number">04 / PROCESS</span>
            <h2>Simple from start to finish.</h2>
          </div>

          <div className="process-grid">
            <div>
              <span>01</span>
              <h3>Tell me what you need</h3>
              <p>
                Send me a message about your business and your idea.
              </p>
            </div>

            <div>
              <span>02</span>
              <h3>Get a clear quote</h3>
              <p>
                I'll recommend an approach and explain what it will cost.
              </p>
            </div>

            <div>
              <span>03</span>
              <h3>I build it</h3>
              <p>
                You see the progress and can give feedback along the way.
              </p>
            </div>

            <div>
              <span>04</span>
              <h3>Go live</h3>
              <p>
                Your finished website is ready for your customers.
              </p>
            </div>
          </div>
        </section>

        <section className="contact-section" id="contact">
          <div className="contact-content">
            <span className="section-number">05 / LET'S WORK</span>

            <h2>Have a project in mind?</h2>

            <p>
              Tell me what you're building, what you need and what your
              business does. I'll get back to you with a clear next step.
            </p>

            <div className="contact-buttons">
              <a
                className="primary-button"
                href="mailto:noticist@icloud.com?subject=Website%20Project"
              >
                <FiMail />
                Email Me
              </a>

              <a
                className="secondary-button"
                href="tel:+16199957334"
              >
                <FiSmartphone />
                Call Me
              </a>
            </div>

            <div className="contact-details">
              <span>noticist@icloud.com</span>
              <span>+1 (619) 995-7334</span>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div>PAVEL BELLE</div>
        <div>Web Developer & Web Designer</div>
        <div>© {new Date().getFullYear()}</div>
      </footer>
    </div>
  );
}